// This version has the Matplotlib plot as the output ?
import React, { useState } from "react";
import { useEffect } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import Plot from "react-plotly.js";
import {
  Box,
  Button,
  Grid,
  TextField,
  Switch,
  Typography,
  MenuItem,
  FormControl,
  Select,
  Paper,
  CircularProgress,
  Alert,
  Stepper,
  Step,
  StepLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  RadioGroup,
  FormControlLabel,
  Radio,
  Menu,
} from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import Tooltip from "@mui/material/Tooltip";
import axios from "axios";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { FormLabel } from "@mui/material";
import { useLocation } from "react-router-dom";

import { HeatingSystemDiagramImage1 } from "./HeatingSystemDiagramImage1";
import { HeatingSystemDiagramImage2 } from "./EWS_Schema";
import { red } from "@mui/material/colors";
import { Divider } from "@mui/material";

const InfoTooltip = ({ title }) => (
  <Tooltip
    title={title}
    arrow
    slotProps={{
      tooltip: {
        sx: {
          fontSize: "0.875rem",
          maxWidth: 280,
        },
      },
    }}
  >
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        cursor: "pointer",
        color: "text.secondary",
      }}
    >
      <InfoIcon fontSize="small" />
    </Box>
  </Tooltip>
);

// Helper to handle empty strings in number fields
const numberField = (msg) =>
  Yup.number()
    .transform((val, orig) => (orig === "" ? null : val))
    .typeError(msg)
    .required(msg);

const heatPumpValidation = Yup.object({
  location: Yup.string().required("PLZ, Ort ist erforderlich"),
  city: Yup.string().required("Standort ist erforderlich"),
  outsideTemp: numberField("Normaußentemperatur ist erforderlich"),
  heatingLoad: numberField("Heizlast ist erforderlich").positive(
    "Heizlast muss positiv sein",
  ),
  numberOfUnits: numberField("Wohneinheiten ist erforderlich").min(
    1,
    "Mindestens 1",
  ),
  numberOfOccupants: numberField("Anzahl der Bewohner ist erforderlich").min(
    1,
    "Mindestens 1",
  ),
  blockingDuration: numberField("Sperrdauer ist erforderlich").min(
    0,
    "Muss 0 oder größer sein",
  ),
  buildingType: Yup.string().required("Gebäudetyp ist erforderlich"),

  heatDemandSpace: Yup.number()
    .transform((val, orig) => (orig === "" ? null : val))
    .nullable()
    .notRequired(),
  heatingLimitTemp: numberField("Heizgrenztemperatur ist erforderlich"),
  heatingType: Yup.string().required("Heizungstyp ist erforderlich"),
  flowTemp: numberField("Vorlauftemperatur ist erforderlich"),
  returnTemp: numberField("Rücklauftemperatur ist erforderlich"),
  shStorageTemp: numberField("Speichertemperatur ist erforderlich"),
  includeDHW: Yup.string().required("Bitte Ja oder Nein auswählen"),
  dhwCalculation: Yup.string().when("includeDHW", {
    is: "yes",
    then: (schema) => schema.required("Berechnung nach ist erforderlich"),
    otherwise: (schema) => schema.notRequired(),
  }),
  dhwStorageTemp: Yup.number()
    .transform((val, orig) => (orig === "" ? null : val))
    .when("includeDHW", {
      is: "yes",
      then: (schema) =>
        schema
          .typeError("TWW Speichertemperatur ist erforderlich")
          .required("TWW Speichertemperatur ist erforderlich"),
      otherwise: (schema) => schema.notRequired(),
    }),
  coldWaterTemp: Yup.number()
    .transform((val, orig) => (orig === "" ? null : val))
    .when("includeDHW", {
      is: "yes",
      then: (schema) =>
        schema
          .typeError("Kaltwassertemperatur ist erforderlich")
          .required("Kaltwassertemperatur ist erforderlich"),
      otherwise: (schema) => schema.notRequired(),
    }),
  pwhTemp: Yup.number()
    .transform((val, orig) => (orig === "" ? null : val))
    .when("includeDHW", {
      is: "yes",
      then: (schema) =>
        schema
          .typeError("Zapftemperatur ist erforderlich")
          .required("Zapftemperatur ist erforderlich"),
      otherwise: (schema) => schema.notRequired(),
    }),
  circulationLosses: Yup.string().when("includeDHW", {
    is: "yes",
    then: (schema) => schema.required("Zirkulationsverluste ist erforderlich"),
    otherwise: (schema) => schema.notRequired(),
  }),
  versorgungskonzept: Yup.string().when("includeDHW", {
    is: "yes",
    then: (schema) => schema.required("Versorgungskonzept ist erforderlich"),
    otherwise: (schema) => schema.notRequired(),
  }),
});

const createPvtValidation = (heatingLoadAtDesignPoint) =>
  Yup.object({
    operationMode: Yup.string().required("Betriebsmodus ist erforderlich"),

    minEvaporatorTemp: Yup.number()
      .transform((val, orig) => (orig === "" ? null : val))
      .when("operationMode", {
        is: (val) => val === "monovalent" || val === "bivalent-parallel",
        then: (schema) =>
          schema
            .typeError(
              "Minimale Verdampfer-Eintrittstemperatur ist erforderlich",
            )
            .required(
              "Minimale Verdampfer-Eintrittstemperatur ist erforderlich",
            ),
        otherwise: (schema) => schema.notRequired(),
      }),

    cop: numberField("COP ist erforderlich"),
    nominalPower: numberField("Nennleistung ist erforderlich"),

    cop_2: Yup.number()
      .transform((val, orig) => (orig === "" ? null : val))
      .when("operationMode", {
        is: "bivalent-parallel",
        then: (schema) =>
          schema
            .typeError("COP (2) ist erforderlich")
            .required("COP (2) ist erforderlich"),
        otherwise: (schema) => schema.notRequired(),
      }),

    nominalPower_2: Yup.number()
      .transform((val, orig) => (orig === "" ? null : val))
      .when("operationMode", {
        is: "bivalent-parallel",
        then: (schema) =>
          schema
            .typeError("Nennleistung (2) ist erforderlich")
            .required("Nennleistung (2) ist erforderlich"),
        otherwise: (schema) => schema.notRequired(),
      }),

    pvtManufacturer: Yup.string().required(
      "PVT-Modul Hersteller ist erforderlich",
    ),
  });

const tempCalculations = {
  floor: {
    getReturnTemp: (flowTemp) => flowTemp - 5,
  },
  radiator: {
    getReturnTemp: (flowTemp) =>
      flowTemp <= 50 ? flowTemp - 7 : flowTemp - 10,
  },
};

const toNumberOrNull = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const disableNumberScroll = (e) => {
  e.target.blur();
};

// Heat pump Design Formik initial values (Default)
const initialHeatPumpValues = {
  projectName: "integraTE-XL",
  location: "97070",
  city: "Würzburg",
  outsideTemp: "-10",
  heatingLoad: "10",
  numberOfUnits: "1",
  numberOfOccupants: "3",
  blockingDuration: "0",
  buildingType: "EFH",
  heatDemandSpace: "",
  heatingLimitTemp: "16",
  heatingType: "radiator",
  flowTemp: "50",
  returnTemp: "",
  heatDemandDHW: "",
  dhwCalculation: "persons",
  dhwStorageTemp: "",
  coldWaterTemp: "10",
  circulationLosses: "",
  dhwFlowTemp: "",
  shStorageTemp: "",
  pwhTemp: "45", //Zapftemp
  circulationTemp: "", //zirkTemp
  minEvaporatorTemp: "-15",
  pvtManufacturer: "DualSun_Finned",
  versorgungskonzept: "wohnungsstation",
  Leistungsminderungsfaktor: "0.25",
  includeDHW: "yes",
};

// PVT Design Formik initial values (Default)
const initialPVTValues = {
  operationMode: "monovalent",
  bivalencePointTemp: 0,
  bivalentParallelB: "",
  minEvaporatorTemp: "-15",
  cop: "",
  nominalPower: "",
  cop_2: "",
  nominalPower_2: "",
  pvtManufacturer: "DualSun_Finned",
};

// BHE Design Formik initial values (Default)
const initialBHEValues = {
  QWP: "8.59",
  QWP_year: "10000",
  lambda_E: "2",
  lEWS: "200",
  bEWS: "6",
  temperatureSpread: 3,
  designTemp: 0,
  cop_WP: 0,
  hasDHW: false,
  resistance: "0.12",
  heatCapacity: "2.18",
  heatFlux: "0.065",
  //layout: '',
  configuration: "rectangular",
};

const valueBoxStyle = {
  border: "1px solid #ccc",
  borderRadius: 1,
  px: 2,
  py: 2,
  height: 36,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontWeight: 700,
  bgcolor: "#fafafa",
  textAlign: "right",
  fontVariantNumeric: "tabular-nums",
};

const FieldLabel = ({ label, tooltip, htmlFor, minHeight = 48 }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "flex-start",
      gap: 0.5,
      mb: 0.5,
      minHeight,
    }}
  >
    <FormLabel
      htmlFor={htmlFor}
      sx={{
        lineHeight: 1.4,
        margin: 0,
        fontSize: (theme) => theme.typography.body2.fontSize,
        fontWeight: 500,
        color: "text.secondary",
      }}
    >
      {label}
    </FormLabel>

    {tooltip ? (
      <Box sx={{ mt: "2px", flexShrink: 0 }}>
        <InfoTooltip title={tooltip} />
      </Box>
    ) : null}
  </Box>
);

const ResultRow = ({ label, value, unit, tooltip, warning }) => (
  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: "1fr 110px 48px",
      alignItems: "start",
      columnGap: 2,
      py: 0.75,
    }}
  >
    <FieldLabel label={label} tooltip={tooltip} minHeight="auto" />

    <Box
      sx={{
        ...valueBoxStyle,
        width: 110,
        justifyContent: "flex-end",
        fontVariantNumeric: "tabular-nums",
        fontFeatureSettings: '"tnum"',
      }}
    >
      {value}
    </Box>

    <Typography variant="body2" sx={{ fontWeight: 700, pt: "6px" }}>
      {unit}
    </Typography>

    {warning ? <Box sx={{ gridColumn: "1 / -1", mt: 1 }}>{warning}</Box> : null}
  </Box>
);

const OpPointRow = ({ children }) => (
  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: {
        xs: "1fr",
        sm: "repeat(2, minmax(180px, 1fr))",
        lg: "repeat(4, minmax(140px, 1fr))",
      },
      gap: 2,
      mt: 2,
      ml: 2,
      pl: 1,
      width: "100%",
      alignItems: "start",
    }}
  >
    {children}
  </Box>
);

const OpPointLabel = ({ text, tooltip }) => (
  <Box
    display="flex"
    alignItems="center"
    gap={0.5}
    sx={{ mt: 2, mb: 1, ml: 2 }}
  >
    <Typography
      variant="subtitle2"
      sx={{ fontWeight: 700, color: "text.secondary" }}
    >
      {text}
    </Typography>

    {tooltip && <InfoTooltip title={tooltip} />}
  </Box>
);

const FormikTextField = ({
  formik,
  name,
  label,
  tooltip,
  type = "text",
  select = false,
  minHeight = 48,
  helperText,
  children,
  ...textFieldProps
}) => {
  const hasError = formik.touched[name] && Boolean(formik.errors[name]);
  const defaultHelperText = formik.touched[name] && formik.errors[name];

  return (
    <>
      <FieldLabel
        label={label}
        tooltip={tooltip}
        htmlFor={name}
        minHeight={minHeight}
      />

      <TextField
        fullWidth
        id={name}
        name={name}
        type={type}
        select={select}
        value={formik.values[name]}
        onChange={formik.handleChange}
        onWheel={type === "number" ? (e) => e.target.blur() : undefined}
        error={hasError}
        helperText={helperText !== undefined ? helperText : defaultHelperText}
        {...textFieldProps}
      >
        {children}
      </TextField>
    </>
  );
};

const sectionCardSx = {
  borderRadius: 1, // softer corners
  border: "1px solid",
  borderColor: "divider",
  bgcolor: "background.paper",
  boxShadow: "0 2px 10px rgba(0,0,0,0.04)",
  overflow: "hidden",
};

const stickyPanelSx = {
  ...sectionCardSx,
  p: 2,
  position: "sticky",
  top: 120,
  "@media (max-width: 1200px)": {
    position: "relative",
    top: "auto",
  },
};

const accordionSx = {
  borderRadius: 4,
  overflow: "hidden",
  border: "1px solid",
  borderColor: "divider",
  boxShadow: "0 2px 10px rgba(0,0,0,0.04)",
  "&:before": {
    display: "none",
  },
};

const DesignResultsPanel = ({
  mode, // "PVT_ONLY" | "PVT_BHE"
  wpDesignResults,
  bheResults,
  displayedHeatingLoadAtDesignPoint,
  displayedPvtCollectorPower,
  displayedRequiredPVTArea,
  displayedHeatingRod,
  showOversizeInfo,
  showHeatingRodWarning,
}) => {
  const isSystemMode = mode === "PVT_ONLY";
  const isBheMode = mode === "PVT_BHE";

  return (
    <Box
      sx={{
        position: "sticky",
        top: 120,
        width: "100%",
        border: "1px solid #ccc",
        borderRadius: 2,
        p: 2,
        bgcolor: "#fff",
        boxShadow: 3,
        zIndex: 10,
        "@media (max-width: 1200px)": {
          position: "relative",
          mt: 2,
          top: "auto",
        },
      }}
    >
      <Box sx={stickyPanelSx}>
        <Typography
          variant="h5"
          gutterBottom
          sx={{ color: "primary.main", fontWeight: 600 }}
        >
          Design Ergebnisse
        </Typography>

        <Grid container spacing={1}>
          <Grid item xs={12}>
            <ResultRow
              label="Erforderliche WP-Leistung"
              value={(wpDesignResults?.requiredHeatPumpPower ?? 0).toFixed(2)}
              unit="kW"
              tooltip="Erforderliche Wärmepumpenleistung zur Deckung des Wärmebedarfs des Gebäudes unter Berücksichtigung der Sperrzeiten"
            />
          </Grid>

          <Grid item xs={12}>
            <ResultRow
              label={
                wpDesignResults?.includeDHW ||
                Number(wpDesignResults?.dhwStorageSize) > 0
                  ? "Gesamte Gebäudeheizlast (TWW + RH)"
                  : "Gesamte Gebäudeheizlast (nur RH)"
              }
              value={(wpDesignResults?.heatingPowerWithDHW ?? 0).toFixed(2)}
              unit="kW"
              tooltip={
                wpDesignResults?.includeDHW ||
                Number(wpDesignResults?.dhwStorageSize) > 0
                  ? "Heizlast inklusive Trinkwarmwasser und Raumheizung, ohne Sperrzeiten"
                  : "Heizlast nur für Raumheizung, ohne Sperrzeiten"
              }
            />
          </Grid>

          {(wpDesignResults?.includeDHW ||
            Number(wpDesignResults?.dhwStorageSize) > 0) && (
            <Grid item xs={12}>
              <ResultRow
                label="TWW-Speichergröße"
                value={(wpDesignResults?.dhwStorageSize ?? 0).toFixed(2)}
                unit="L"
                tooltip="Größe des Trinkwarmwasserspeichers nach VDI 4645"
              />
            </Grid>
          )}

          <Grid item xs={12}>
            <ResultRow
              label="RH-Speichergröße"
              value={(wpDesignResults?.rhStorageSize ?? 0).toFixed(2)}
              unit="L"
              tooltip="Größe des Speichers für die Raumheizung"
            />
          </Grid>

          {/* System Design Section */}
          {isSystemMode && (
            <>
              <Grid item xs={12}>
                <ResultRow
                  label="Heizlast am Auslegungspunkt"
                  value={(displayedHeatingLoadAtDesignPoint ?? 0).toFixed(2)}
                  unit="kW"
                  tooltip="Heizlast inklusive Raumheizung und Trinkwarmwasser am Auslegungspunkt"
                />
              </Grid>

              <Grid item xs={12}>
                <ResultRow
                  label="Thermische PVT-Kollektorleistung"
                  value={(displayedPvtCollectorPower ?? 0).toFixed(2)}
                  unit="kW/m²"
                  tooltip="Spezifische thermische Leistung der PVT-Kollektoren"
                />
              </Grid>

              <Grid item xs={12}>
                <ResultRow
                  label="Erforderliche PVT-Fläche"
                  value={(displayedRequiredPVTArea ?? 0).toFixed(2)}
                  unit="m²"
                  tooltip="PVT-Fläche, die im System installiert werden soll"
                />
              </Grid>

              {/* {showOversizeInfo && (
                <Grid item xs={12}>
                  <Alert severity="info" sx={{ mt: 1 }}>
                    Hinweis: Die WP-Leistung ist größer als die Heizlast am
                    Auslegungspunkt. Es wird keine zusätzliche PVT-Fläche über{" "}
                    {(displayedRequiredPVTArea ?? 0).toFixed(2)} m² benötigt.
                  </Alert>
                </Grid>
              )} */}

              <Grid item xs={12}>
                <ResultRow
                  label="Erforderlicher Heizstab"
                  value={(displayedHeatingRod ?? 0).toFixed(2)}
                  unit="kW"
                  tooltip="Zusätzliche Heizleistung, falls die Wärmepumpe am Auslegungspunkt nicht ausreicht"
                />
              </Grid>

              {showHeatingRodWarning && (
                <Grid item xs={12}>
                  <Alert
                    severity="warning"
                    sx={{
                      mt: 0.5,
                      fontWeight: "bold",
                      color: red[700],
                      bgcolor: red[50],
                    }}
                  >
                    Es scheint, dass ein zweiter Wärmeerzeuger notwendig ist.
                  </Alert>
                </Grid>
              )}
            </>
          )}

          {/* BHE / EWS Section */}
          {isBheMode && (
            <>
              <Grid item xs={12}>
                <ResultRow
                  label="Gesamtlänge der Erdwärmesonden"
                  value={(bheResults?.LEWS ?? 0).toFixed(2)}
                  unit="m"
                  tooltip="Gesamtlänge der Erdwärmesonden zur Deckung des Wärmebedarfs an der Wärmepumpenquelle"
                />
              </Grid>

              {/* <Grid item xs={12}>
                <ResultRow
                  label="Tiefe der Erdwärmesonden"
                  value={(bheResults?.lengthPerBHE ?? 0).toFixed(2)}
                  unit="m"
                  tooltip="Tiefe jeder einzelnen Erdwärmesonde"
                />
              </Grid> */}

              <Grid item xs={12}>
                <ResultRow
                  label="Anzahl der Erdwärmesonden"
                  value={(bheResults?.nEWS ?? 0).toFixed(2)}
                  unit=""
                  tooltip="Anzahl der erforderlichen Erdwärmesonden basierend auf der maximal zulässigen Tiefe"
                />
              </Grid>

              <Grid item xs={12}>
                <ResultRow
                  label="Spezifischer Wärmeentzug"
                  value={(bheResults?.qEWS_H ?? 0).toFixed(2)}
                  unit="W/m"
                  tooltip="Spezifische Wärmeentzugsleistung pro Meter Erdwärmesonde"
                />
              </Grid>

              {/* <Grid item xs={12}>
                <ResultRow
                  label="Spezifischer Wärmeeintrag"
                  value={(bheResults?.qEWS_K ?? 0).toFixed(2)}
                  unit="W/m"
                  tooltip="Spezifische Wärmeeintragsleistung pro Meter Erdwärmesonde"
                />
              </Grid> */}

              <Grid item xs={12}>
                <ResultRow
                  label="Min. Rücklauftemperatur EWS"
                  value={(bheResults?.minRLEWS ?? 0).toFixed(2)}
                  unit="°C"
                  tooltip="Minimale Rücklauftemperatur im EWS-System"
                />
              </Grid>

              <Grid item xs={12}>
                <ResultRow
                  label="Max. Rücklauftemperatur EWS"
                  value={(bheResults?.maxRLEWS ?? 0).toFixed(2)}
                  unit="°C"
                  tooltip="Maximale Rücklauftemperatur im EWS-System"
                />
              </Grid>

              {/* <Grid item xs={12}>
                <ResultRow
                  label="Optimale regenerierte Anteil der Wärmeentzug"
                  value={(bheResults?.regeneratedEnergy ?? 0).toFixed(2)}
                  unit="%"
                  tooltip="Optimale Wärmemenge, die in das Erdreich eingespeist werden kann"
                />
              </Grid>

              <Grid item xs={12}>
                <ResultRow
                  label="Verkürzung der Erdwärmesondenlänge"
                  value={(bheResults?.percentageReduction ?? 0).toFixed(2)}
                  unit="%"
                  tooltip="Verkürzung der Sondenlänge mit Regeneration durch PVT-Kollektoren"
                />
              </Grid> */}
            </>
          )}
        </Grid>
      </Box>
    </Box>
  );
};
const PVTDesignForm = ({ activeStep, setActiveStep, scrollToSection }) => {
  const location = useLocation();
  const mode = location.state?.mode ?? "PVT_ONLY";

  const isPvtMode = mode === "PVT_ONLY";
  const isBheMode = mode === "PVT_BHE";

  // Wärmepumpe should be shown in both modes
  const showWP = true;
  const showPVT = isPvtMode;
  const showEWS = isBheMode;

  const [tryMeta, setTryMeta] = useState(null);
  const [tryLoading, setTryLoading] = useState(false);
  const [tryError, setTryError] = useState(null);

  const [wpDesignResults, setWpDesignResults] = useState({
    requiredHeatPumpPower: 0,
    heatingPowerWithDHW: 0,
    dhwStorageSize: 0,
    rhStorageSize: 0,
    copDefault: 0,
    nominalPowerDefault: 0,
    heatingLoadAtDesignPoint: 0,
    pvtCollectorPower: 0,
    requiredPVTArea: 0,
    requiredHeatingRod: 0,
  });
  const [systemDesignResults, setSystemDesignResults] = useState({
    heatingLoadAtDesignPoint: 0,
    heatPumpPower: 0,
    pvtCollectorPower: 0,
    requiredPVTArea: 0,
    thermalPowerOfPVT: 0,
    requiredHeatingRod: 0,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState({
    project: true,
    building: true,
    roomHeating: true,
    hotWater: true,
    operationMode: true,
    heatPumpSpecs: true,
    pvtModule: true,
    bheSystem: true,
    groundProperties: true,
    fieldProperties: true,
  });

  const [bheResults, setBheResults] = useState({
    LEWS: 0,
    lengthPerBHE: 0,
    nEWS: 0,
    qEWS_H: 0,
    qEWS_K: 0,
    minRLEWS: 0,
    maxRLEWS: 0,
    regeneratedEnergy: 0,
    percentageReduction: 0,
    data: {
      length: [],
      Max_Fluids: [],
      Min_Fluids: [],
      cooling_load_range: [],
      qdot_H: [],
      qdot_K: [],
    },
  });
  const [bheLoading, setBheLoading] = useState(false);
  const [bheError, setBheError] = useState(null);

  const STEP_IDS = {
    1: "wp-step",
    2: "pvt-step",
    3: "ews-step",
    4: "results-step",
  };

  const [activeCalculation, setActiveCalculation] = useState(null);
  // "wp" | "system" | null

  const getSuggestedCop = ({ B, condenserTemp }) => {
    const b = Number(B);
    const w = Number(condenserTemp);

    if (!Number.isFinite(b) || !Number.isFinite(w)) return "";

    if (w === 35) return 0.0027 * b * b + 0.1438 * b + 4.5369;
    if (w === 40) return 0.0021 * b * b + 0.1185 * b + 4.051;
    if (w === 45) return 0.0014 * b * b + 0.0933 * b + 3.5651;
    if (w === 50) return 0.0011 * b * b + 0.078 * b + 3.1745;
    if (w === 55) return 0.0008 * b * b + 0.0626 * b + 2.784;
    if (w === 60) return 0.0005 * b * b + 0.0473 * b + 2.3935;
    if (w === 65) return 0.0002 * b * b + 0.0319 * b + 2.0029;

    return "";
  };
  const getCondenserTemp = () => {
    const hasDHW = heatPumpFormik.values.includeDHW === "yes";
    const sh = Number(heatPumpFormik.values.shStorageTemp);
    const dhw = Number(heatPumpFormik.values.dhwStorageTemp);

    if (hasDHW) {
      if (Number.isFinite(sh) && Number.isFinite(dhw)) return Math.max(sh, dhw);
      if (Number.isFinite(sh)) return sh;
      if (Number.isFinite(dhw)) return dhw;
      return "";
    }

    return Number.isFinite(sh) ? sh : "";
  };

  const [cop2ManuallyEdited, setCop2ManuallyEdited] = useState(false);

  // Manages a form "heatPumpFormik" for designing a heat pump system, validates input,
  // submits it to a backend API (See the server.js which then calls the functions in Calculate.js),
  //  and handles the responseHeat pump Design Formik
  const heatPumpFormik = useFormik({
    initialValues: initialHeatPumpValues,
    //validationSchema: heatPumpValidation,
    validateOnBlur: false,
    validateOnChange: false,

    onSubmit: async (values) => {
      console.log("Form values being submitted:", values);
      console.log("Payload size:", JSON.stringify(values).length);
      console.log("Frontend values:", values);

      const dhwEnabled = values.includeDHW === "yes";

      const requestData = {
        ...values, // heatPumpFormik inputs
        includeDHW: values.includeDHW,
        minEvaporatorTemp: pvtFormik.values.minEvaporatorTemp,
        pvtManufacturer: pvtFormik.values.pvtManufacturer,

        // Only send DHW values when enabled
        dhwCalculation: dhwEnabled ? values.dhwCalculation : null,
        dhwStorageTemp: dhwEnabled ? values.dhwStorageTemp : null,
        coldWaterTemp: dhwEnabled ? values.coldWaterTemp : null,
        circulationLosses: dhwEnabled ? values.circulationLosses : null,
        versorgungskonzept: dhwEnabled ? values.versorgungskonzept : null,
        pwhTemp: dhwEnabled ? values.pwhTemp : null,
        circulationTemp: dhwEnabled ? values.circulationTemp : null,
        heatDemandDHW: dhwEnabled ? values.heatDemandDHW : null,
      };
      setLoading(true);
      setError(null);

      try {
        const response = await axios.post(
          "/api/calculate-wp-design",
          requestData,
        );

        if (response.data && response.data.success) {
          const results = response.data.results;
          setWpDesignResults(response.data.results);
          // fill system defaults from backend WP calculation
          if (Number.isFinite(Number(results.copDefault))) {
            pvtFormik.setFieldValue(
              "cop",
              Number(results.copDefault).toFixed(3),
              false,
            );
          }

          if (Number.isFinite(Number(results.nominalPowerDefault))) {
            pvtFormik.setFieldValue(
              "nominalPower",
              Number(results.nominalPowerDefault).toFixed(3),
              false,
            );
          }
        } else {
          setError(response.data.error || "Unknown error");
        }
      } catch (error) {
        setError(error.response?.data?.error || error.message);
      } finally {
        setLoading(false);
      }
    },
  });

  const buildSystemPayload = (values) => {
    const mode = values.operationMode;
    const dhwEnabled = heatPumpFormik.values.includeDHW === "yes";

    const base = {
      operationMode: values.operationMode,
      minEvaporatorTemp: values.minEvaporatorTemp,
      bivalencePointTemp: values.bivalencePointTemp,
      cop: values.cop,
      nominalPower: values.nominalPower,
      pvtManufacturer: values.pvtManufacturer,
    };

    const modeSpecific =
      mode === "bivalent-parallel"
        ? {
            cop_2: values.cop_2,
            nominalPower_2: values.nominalPower_2,
          }
        : {};

    return {
      ...heatPumpFormik.values,
      includeDHW: heatPumpFormik.values.includeDHW,
      ...base,
      ...modeSpecific,
      pvtManufacturer: values.pvtManufacturer,
      plz: (heatPumpFormik.values.location || "").trim(),
      wpDesignResults,

      // sanitize DHW fields
      dhwCalculation: dhwEnabled ? heatPumpFormik.values.dhwCalculation : null,
      dhwStorageTemp: dhwEnabled ? heatPumpFormik.values.dhwStorageTemp : null,
      coldWaterTemp: dhwEnabled ? heatPumpFormik.values.coldWaterTemp : null,
      circulationLosses: dhwEnabled
        ? heatPumpFormik.values.circulationLosses
        : null,
      versorgungskonzept: dhwEnabled
        ? heatPumpFormik.values.versorgungskonzept
        : null,
      pwhTemp: dhwEnabled ? heatPumpFormik.values.pwhTemp : null,
      circulationTemp: dhwEnabled
        ? heatPumpFormik.values.circulationTemp
        : null,
      heatDemandDHW: dhwEnabled ? heatPumpFormik.values.heatDemandDHW : null,
    };
  };

  // PVT Collector Design Formik
  const pvtFormik = useFormik({
    initialValues: initialPVTValues,
    validationSchema: createPvtValidation(
      wpDesignResults.heatingLoadAtDesignPoint,
    ),
    onSubmit: async (values) => {
      console.log("PVT Formik submit triggered");
      setLoading(true);
      setError(null);
      try {
        const payload = buildSystemPayload(values);

        console.log("Payload sent to backend:", payload);

        const response = await axios.post(
          "/api/calculate-system-design",
          payload,
        );

        console.log(" Backend response:", response.data);

        if (response.data && response.data.success) {
          setSystemDesignResults(response.data.results);
        } else {
          setError(response.data.error || "Unknown error");
        }
      } catch (error) {
        console.error("Backend error:", error.response?.data || error.message);
        setError(error.response?.data?.error || error.message);
      } finally {
        setLoading(false);
      }
    },
  });
  const isEmpty = (v) => v === "" || v === null || v === undefined;
  const SystemCalcDisabled =
    loading ||
    isEmpty(pvtFormik.values.operationMode) ||
    isEmpty(pvtFormik.values.cop) ||
    isEmpty(pvtFormik.values.nominalPower) ||
    (pvtFormik.values.operationMode === "bivalent-parallel" &&
      (isEmpty(pvtFormik.values.cop_2) ||
        isEmpty(pvtFormik.values.nominalPower_2)));

  const opMode = pvtFormik.values.operationMode;
  const isModeSelected = !!opMode;
  const heatingLoadAtDesign = Number(
    systemDesignResults?.heatingLoadAtDesignPoint ?? 0,
  );
  const heatPumpPowerAtDesign = Number(
    systemDesignResults?.heatPumpPower ?? pvtFormik.values.nominalPower ?? 0,
  );
  const EPSILON = 0.01; // 10 W threshold
  // deficit at design point (kW)
  const deficitAtDesignRaw = Math.max(
    0,
    heatingLoadAtDesign - heatPumpPowerAtDesign,
  );

  const deficitAtDesign = deficitAtDesignRaw < EPSILON ? 0 : deficitAtDesignRaw;

  const displayedHeatingRod = ["monovalent"].includes(opMode)
    ? deficitAtDesign
    : Number(systemDesignResults?.requiredHeatingRod ?? 0);

  const showHeatingRodWarning =
    ["monovalent"].includes(opMode) && displayedHeatingRod >= EPSILON;

  // PVT Area cap so you don't oversize beyond design Heizlast
  const pvtSpecificPower = Number(systemDesignResults?.pvtCollectorPower ?? 0); // kW/m²
  const requiredPVTAreaRaw = Number(systemDesignResults?.requiredPVTArea ?? 0);

  // cap area to what is needed for Heizlast, not more
  const requiredPVTAreaCap =
    pvtSpecificPower > 0
      ? heatingLoadAtDesign / pvtSpecificPower
      : requiredPVTAreaRaw;

  const displayedPVTAreaWp = Number(wpDesignResults?.requiredPVTArea ?? 0);
  const displayedPVTAreaSystem = Math.min(
    requiredPVTAreaRaw,
    requiredPVTAreaCap,
  );

  const displayedPVTArea =
    activeCalculation === "wp"
      ? displayedPVTAreaWp
      : activeCalculation === "system"
        ? displayedPVTAreaSystem
        : 0;

  // If WP is oversized, tell user they don't need more area
  const showOversizeInfo =
    ["monovalent", "bivalent-parallel"].includes(opMode) &&
    heatPumpPowerAtDesign > heatingLoadAtDesign &&
    heatingLoadAtDesign > 0;

  const displayedHeatingLoadAtDesignPoint =
    activeCalculation === "system"
      ? Number(systemDesignResults?.heatingLoadAtDesignPoint ?? 0)
      : Number(wpDesignResults?.heatingLoadAtDesignPoint ?? 0);

  const displayedPvtCollectorPower =
    activeCalculation === "system"
      ? Number(systemDesignResults?.pvtCollectorPower ?? 0)
      : Number(wpDesignResults?.pvtCollectorPower ?? 0);

  const displayedRequiredPVTArea =
    activeCalculation === "system"
      ? Number(displayedPVTAreaSystem ?? 0)
      : Number(wpDesignResults?.requiredPVTArea ?? 0);

  const displayedHeatingRodFinal =
    activeCalculation === "system"
      ? Number(displayedHeatingRod ?? 0)
      : Number(wpDesignResults?.requiredHeatingRod ?? 0);

  // BHE Design Formik running with python script
  const bheFormik2 = useFormik({
    initialValues: initialBHEValues,
    onSubmit: async (values) => {
      setBheLoading(true);
      setBheError(null);
      setBheResults(null);
      try {
        console.log("Sending BHE calculation request with values:", values);

        const response = await axios.post("/api/calculate-bhe", {
          ...values,
          ...heatPumpFormik.values,
          ...pvtFormik.values,
          wpDesignResults,
          systemDesignResults,
        });

        console.log("BHE calculation response:", response.data);

        if (response.data && response.data.success) {
          setBheResults(response.data.results);
        } else {
          setBheError(response.data.error || "Unknown error");
        }
      } catch (error) {
        console.error("BHE calculation error:", error);
        console.error("Error response:", error.response);
        setBheError(
          error.response?.data?.error ||
            error.message ||
            "Network error occurred",
        );
      } finally {
        setBheLoading(false);
      }
    },
  });

  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpanded({ ...expanded, [panel]: isExpanded });
  };

  // Helper to scroll to a section by id
  const handleSidebarNav = (sectionId) => {
    const el = document.getElementById(sectionId);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  // Helper to get W (highest of DHW and room heating storage temp) THIS HAS TO BE CHANGED TO DIVIDE WP FOR SH AND DHW
  const getWValue = () => {
    const dhw = Number(heatPumpFormik.values.dhwStorageTemp);
    const rh = Number(heatPumpFormik.values.shStorageTemp);
    if (!isNaN(dhw) && !isNaN(rh)) return Math.max(dhw, rh);
    if (!isNaN(dhw)) return dhw;
    if (!isNaN(rh)) return rh;
    return "";
  };

  const { length, Max_Fluids, Min_Fluids, cooling_load_range, qdot_H, qdot_K } =
    bheResults?.data ?? {};

  const handleDownload = () => {
    const plotEl = document.getElementById("bhe-plot");
    if (plotEl) {
      window.Plotly.downloadImage(plotEl, {
        format: "png",
        filename: "bhe_plot",
        height: 600,
        width: 800,
      });
    }
  };
  useEffect(() => {
    if (pvtFormik.values.operationMode !== "bivalent-parallel") return;

    const b2 = Number(pvtFormik.values.bivalentParallelB);
    if (!Number.isFinite(b2)) return;

    const nextBivalencePoint = b2 + 5;

    if (Number(pvtFormik.values.bivalencePointTemp) !== nextBivalencePoint) {
      pvtFormik.setFieldValue(
        "bivalencePointTemp",
        String(nextBivalencePoint),
        false,
      );
    }
  }, [pvtFormik.values.operationMode, pvtFormik.values.bivalentParallelB]);

  useEffect(() => {
    const id = STEP_IDS[activeStep];
    if (!id) return;

    // delay so layout exists
    setTimeout(() => {
      document.getElementById(id)?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 50);
  }, [activeStep]);

  // Prevent parallel values from lingering into monovalnet submission
  useEffect(() => {
    const m = pvtFormik.values.operationMode;

    // when leaving bivalent-parallel, clear 2nd operating point
    if (m !== "bivalent-parallel") {
      if (pvtFormik.values.cop_2 !== "") pvtFormik.setFieldValue("cop_2", "");
      if (pvtFormik.values.nominalPower_2 !== "")
        pvtFormik.setFieldValue("nominalPower_2", "");
      if (pvtFormik.values.bivalentParallelB !== "") {
        pvtFormik.setFieldValue("bivalentParallelB", "", false);
      }
    }

    // optional: if you want clean defaults when mode changes
    // you can also reset minEvaporatorTemp/bivalencePointTemp rules here if needed
  }, [pvtFormik.values.operationMode]);

  const steps = showEWS
    ? [
        { label: "Wärmepumpe", step: 1 },
        { label: "EWS_Design", step: 3 },
      ]
    : [
        { label: "wärmepumpe", step: 1 },
        { label: "PVT_Kollektoren", step: 2 },
      ];

  const activeIndex = Math.max(
    0,
    steps.findIndex((s) => s.step === activeStep),
  );

  useEffect(() => {
    const mode = pvtFormik.values.operationMode;
    const minEv = Number(pvtFormik.values.minEvaporatorTemp);

    if (mode === "bivalent-alternative") {
      pvtFormik.setFieldValue(
        "bivalencePointTemp",
        Number.isFinite(minEv) ? minEv + 5 : "",
        false,
      );
    }
  }, [pvtFormik.values.operationMode, pvtFormik.values.minEvaporatorTemp]);

  //derive returnTemp + shStorageTemp whenever user chagnes heatingType or flowTemp
  useEffect(() => {
    const heatingType = heatPumpFormik.values.heatingType; // "radiator" | "floor"
    const flow = toNumberOrNull(heatPumpFormik.values.flowTemp);

    // If user hasn't selected both yet, don't overwrite anything
    if (!heatingType || flow === null) return;

    // 1) storage temp equals flow temp
    const nextStorage = flow;
 
    // 2) return temp depends on heating type + flow
    const calc = tempCalculations[heatingType];
    const nextReturn = calc ? calc.getReturnTemp(flow) : null;

    // Avoid infinite loops: only set if changed
    if (toNumberOrNull(heatPumpFormik.values.shStorageTemp) !== nextStorage) {
      heatPumpFormik.setFieldValue("shStorageTemp", String(nextStorage), false);
    }

    if (
      nextReturn !== null &&
      toNumberOrNull(heatPumpFormik.values.returnTemp) !== nextReturn
    ) {
      heatPumpFormik.setFieldValue("returnTemp", String(nextReturn), false);
    }
  }, [heatPumpFormik.values.heatingType, heatPumpFormik.values.flowTemp]);

  const dhwConfig = {
    concept1: { offset: 10, circulationFrom: "pwh" },
    concept2: { offset: 10, circulationFrom: "pwh" },
    concept3: { offset: 15, circulationFrom: "storage" },
  };

  useEffect(() => {
    if (heatPumpFormik.values.includeDHW !== "yes") return;

    const zapf = Number(heatPumpFormik.values.pwhTemp);
    const versorgung = heatPumpFormik.values.versorgungskonzept;

    if (!Number.isFinite(zapf)) return;

    const config = dhwConfig[versorgung] ?? {
      offset: 10,
      circulationFrom: "storage",
    };

    const nextDhwStorage = zapf + config.offset;

    const nextCirculationTemp =
      config.circulationFrom === "pwh" ? zapf - 5 : nextDhwStorage - 5;

    if (Number(heatPumpFormik.values.dhwStorageTemp) !== nextDhwStorage) {
      heatPumpFormik.setFieldValue("dhwStorageTemp", nextDhwStorage, false);
    }

    if (Number(heatPumpFormik.values.circulationTemp) !== nextCirculationTemp) {
      heatPumpFormik.setFieldValue(
        "circulationTemp",
        String(nextCirculationTemp),
        false,
      );
    }
  }, [
    heatPumpFormik.values.includeDHW,
    heatPumpFormik.values.pwhTemp,
    heatPumpFormik.values.versorgungskonzept,
  ]);

  // If user selects wohnungsstation, we assume no circulation losses,
  //  otherwise we assume there are circulation losses.
  useEffect(() => {
    if (heatPumpFormik.values.includeDHW !== "yes") return;

    const versorgung = heatPumpFormik.values.versorgungskonzept;

    if (!versorgung) return;

    if (versorgung === "wohnungsstation") {
      if (heatPumpFormik.values.circulationLosses !== "no") {
        heatPumpFormik.setFieldValue("circulationLosses", "no", false);
      }
    } else {
      if (heatPumpFormik.values.circulationLosses !== "yes") {
        heatPumpFormik.setFieldValue("circulationLosses", "yes", false);
      }
    }
  }, [
    heatPumpFormik.values.includeDHW,
    heatPumpFormik.values.versorgungskonzept,
  ]);

  useEffect(() => {
    if (pvtFormik.values.operationMode !== "bivalent-parallel") return;

    const B2 = Number(pvtFormik.values.bivalentParallelB);
    const condenserTemp = getCondenserTemp();

    if (!Number.isFinite(B2) || !Number.isFinite(Number(condenserTemp))) return;

    const suggestedCop = getSuggestedCop({
      B: B2,
      condenserTemp,
    });

    if (suggestedCop === "") return;

    if (!cop2ManuallyEdited) {
      pvtFormik.setFieldValue("cop_2", Number(suggestedCop).toFixed(2), false);
    }
  }, [
    pvtFormik.values.operationMode,
    pvtFormik.values.bivalentParallelB,
    heatPumpFormik.values.includeDHW,
    heatPumpFormik.values.shStorageTemp,
    heatPumpFormik.values.dhwStorageTemp,
    cop2ManuallyEdited,
  ]);
  useEffect(() => {
    if (pvtFormik.values.operationMode !== "bivalent-parallel") {
      setCop2ManuallyEdited(false);
      return;
    }

    if (!pvtFormik.values.bivalentParallelB) {
      setCop2ManuallyEdited(false);
    }
  }, [pvtFormik.values.operationMode, pvtFormik.values.bivalentParallelB]);

  useEffect(() => {
    const qwp = Number(wpDesignResults?.requiredHeatPumpPower);
    const copWp = Number(wpDesignResults?.copDefault);

    if (Number.isFinite(qwp)) {
      bheFormik2.setFieldValue("QWP", qwp.toFixed(2), false);
    }

    if (Number.isFinite(copWp) && copWp > 0) {
      bheFormik2.setFieldValue("cop_WP", copWp.toFixed(2), false);
    }
  }, [
    wpDesignResults?.requiredHeatPumpPower,
    wpDesignResults?.copDefault,
  ]);

  return (
    <Box sx={{ width: "100%", px: { xs: 1, sm: 3 }, pt: 2, pb: 10 }}>
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 5,
          bgcolor: "background.default",
          py: 1,
        }}
      >
        <Stepper activeStep={activeIndex} sx={{ mb: 2 }}>
          {steps.map((s) => (
            <Step
              key={s.label}
              sx={{ cursor: "pointer" }}
              onClick={() => setActiveStep(s.step)}
            >
              <StepLabel>{s.label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </Box>

      {error && (
        <Paper sx={{ p: 2, mb: 2, bgcolor: "#ffebee" }}>
          <Typography color="error">{error}</Typography>
        </Paper>
      )}
      <Grid container spacing={3} alignItems="flex-start">
        <Grid item xs={12} lg={5}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            {showWP && (
              <Box id="wp-step" sx={{ scrollMarginTop: "210px", mb: 6 }}>
                {/* Form */}
                <form onSubmit={heatPumpFormik.handleSubmit}>
                  <Typography
                    variant="h4"
                    sx={{ color: "primary.main", fontWeight: 600, mb: 2 }}
                  >
                    Auslegungsparameter der Wärmepumpe
                  </Typography>
                  {/*OUTER 2-COLUMN LAYOUT */}
                  <Grid container spacing={3} alignItems="flex-start">
                    {/* LEFT column: all accordions */}
                    <Grid item xs={12} lg={12}>
                      {/* Project Information */}
                      <Grid container spacing={3} alignItems="flex-start">
                        <Grid item xs={12}>
                          <Accordion
                            expanded={expanded.project}
                            onChange={handleAccordionChange("project")}
                            id="project"
                            sx={accordionSx}
                          >
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                              <Typography variant="h6">
                                Projectinformation
                              </Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                              <Grid container spacing={1}>
                                <Grid item xs={12} md={4}>
                                  <FormikTextField
                                    formik={heatPumpFormik}
                                    name="projectName"
                                    label="Projektname"
                                    tooltip="Geben Sie den Namen des Projekts ein, z.B. Gebäudebezeichnung oder Standort"
                                  />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                  <FormikTextField
                                    formik={heatPumpFormik}
                                    name="location"
                                    label="PLZ"
                                    tooltip="Bitte geben Sie die Postleitzahl und den Ort des Projekts ein. Die entsprechenden Wetter- und Erdreich-Eigenschaften werden basierend auf der PLZ ausgewählt. "
                                  />
                                </Grid>

                                <Grid item xs={12} md={4}>
                                  <FormikTextField
                                    formik={heatPumpFormik}
                                    name="city"
                                    label="Standort"
                                    tooltip="Bitte geben Sie den Standort (Stadt) des Projekts ein"
                                  />
                                </Grid>
                              </Grid>
                            </AccordionDetails>
                          </Accordion>
                        </Grid>
                        <Box sx={{ mt: 8 }} />
                        {/* Building & System */}
                        <Grid item xs={12}>
                          <Accordion
                            expanded={expanded.building}
                            onChange={handleAccordionChange("building")}
                            id="building"
                            sx={accordionSx}
                          >
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                              <Typography variant="h6">
                                Gebäude und System
                              </Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                              <Grid container spacing={1}>
                                {/* Normaußentemperatur  */}
                                <Grid item xs={12} md={4}>
                                  <FormikTextField
                                    formik={heatPumpFormik}
                                    name="outsideTemp"
                                    label="Normaußen temperatur (°C)"
                                    type="number"
                                    minHeight={48}
                                    tooltip={
                                      <>
                                        Die minimale Temperatur eines
                                        geografischen Ortes, die innerhalb von
                                        20 Jahren zehnmal an mindestens zwei
                                        aufeinanderfolgenden Tagen eingehalten
                                        wurde.
                                        <br />
                                        Es kann über diesen Link berechnet
                                        werden:{" "}
                                        <a
                                          href="https://www.waermepumpe.de/werkzeuge/klimakarte/"
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          style={{
                                            color: "lightblue",
                                            textDecoration: "underline",
                                          }}
                                        >
                                          Klimakarte
                                        </a>
                                      </>
                                    }
                                  />
                                </Grid>

                                <Grid item xs={12} md={4}>
                                  <FormikTextField
                                    formik={heatPumpFormik}
                                    name="heatingLoad"
                                    label="Heizlast (kW)"
                                    type="number"
                                    minHeight={48}
                                    tooltip={
                                      <>
                                        Vorhandene Heizlast des Gebäudes bei
                                        Normaußentemperatur.
                                        <br />
                                        Wenn nicht vorhanden, kann sie über
                                        diesen Link vom Bundesverband Wärmepumpe
                                        berechnet werden:{" "}
                                        <a
                                          href="https://www.waermepumpe.de/werkzeuge/heizlastrechner/"
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          style={{
                                            color: "lightblue",
                                            textDecoration: "underline",
                                          }}
                                        >
                                          Heizlastrechner
                                        </a>
                                      </>
                                    }
                                  />
                                </Grid>

                                <Grid item xs={12} md={4}>
                                  <FormikTextField
                                    formik={heatPumpFormik}
                                    name="buildingType"
                                    label="Gebäudetyp"
                                    select
                                    minHeight={48}
                                    tooltip="Bitte Wählen sie zwischen den folgenden Gebäudetypen, die für die Berechnung der Heizlast verwendet werden sollen"
                                  >
                                    <MenuItem value="EFH">
                                      Einfamilienhaus (EFH)
                                    </MenuItem>
                                    <MenuItem value="MFH">
                                      Mehrfamilienhaus (MFH)
                                    </MenuItem>
                                    <MenuItem value="RH">
                                      Reihenhaus (RH)
                                    </MenuItem>
                                    <MenuItem value="GMH">
                                      Größes Mehrfamilienhaus (GMH)
                                    </MenuItem>
                                    <MenuItem value="HH">
                                      Hochhaus (HH)
                                    </MenuItem>
                                  </FormikTextField>
                                </Grid>

                                <Grid item xs={12} md={4}>
                                  <FormikTextField
                                    formik={heatPumpFormik}
                                    name="numberOfUnits"
                                    label="Wohneinheiten (Nr.)"
                                    type="number"
                                    minHeight={48}
                                    inputProps={{ min: 1 }}
                                    tooltip="Anzahl der Wohnungen in einem Mehrfamilienhaus (Wenn Wohneinheit 1 ist, dann ist es als ein Einfamilienhaus betrachtet)"
                                  />
                                </Grid>

                                <Grid item xs={12} md={4}>
                                  <FormikTextField
                                    formik={heatPumpFormik}
                                    name="numberOfOccupants"
                                    label="Anzahl der Bewohner (Nr.)"
                                    type="number"
                                    minHeight={48}
                                    inputProps={{ min: 1 }}
                                    tooltip="Anzahl der Personen, die im Gebäude leben (diese Angabe wird nur verwendet, wenn das TWW nach der Anzahl der Personen berechnet werden soll)"
                                  />
                                </Grid>

                                <Grid item xs={12} md={4}>
                                  <FormikTextField
                                    formik={heatPumpFormik}
                                    name="heatingLimitTemp"
                                    label="Heizgrenztemperatur (°C)"
                                    type="number"
                                    minHeight={48}
                                    helperText="Standardwert: 16°C"
                                    tooltip="Die Außentemperatur, bei der sich das Heizsystem ohne Wärmebedarf abschalten kann"
                                  />
                                </Grid>
                              </Grid>
                            </AccordionDetails>
                          </Accordion>
                        </Grid>
                        <Box sx={{ mt: 4 }} />
                        {/* Room Heating */}
                        <Grid item xs={12}>
                          <Accordion
                            expanded={expanded.roomHeating}
                            onChange={handleAccordionChange("roomHeating")}
                            id="roomHeating"
                            sx={accordionSx}
                          >
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                              <Typography variant="h6">Raumheizung</Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                              <Grid container spacing={1}>
                                {/* Heating Type */}
                                <Grid item xs={12} md={4}>
                                  <FormikTextField
                                    formik={heatPumpFormik}
                                    name="heatingType"
                                    label="Heizungstyp"
                                    select
                                    minHeight={48}
                                    tooltip="Wie wird das Gebäude beheizt?"
                                  >
                                    <MenuItem value="radiator">
                                      Heizkörper
                                    </MenuItem>
                                    <MenuItem value="floor">Fußboden</MenuItem>
                                  </FormikTextField>
                                </Grid>

                                {/* Flow Temperature */}
                                <Grid item xs={12} md={4}>
                                  <FormikTextField
                                    formik={heatPumpFormik}
                                    name="flowTemp"
                                    label="Vorlauftemperatur (°C)"
                                    type="number"
                                    minHeight={48}
                                    tooltip="Die Vorlauf- und Rücklauftemperaturen des Heizkreis"
                                  />
                                </Grid>
                              </Grid>
                            </AccordionDetails>
                          </Accordion>
                        </Grid>

                        <Box sx={{ mt: 4 }} />
                        {/* Hot Drinking Water */}
                        <Grid item xs={12}>
                          <Accordion
                            expanded={expanded.hotWater}
                            onChange={handleAccordionChange("hotWater")}
                            id="hotWater"
                            sx={accordionSx}
                          >
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                              <Box
                                sx={{
                                  width: "100%",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "space-between",
                                  pr: 2,
                                }}
                              >
                                <Typography variant="h6">
                                  Trinkwarmwasser
                                </Typography>

                                <Box
                                  onClick={(e) => e.stopPropagation()}
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1.5,
                                  }}
                                >
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                  >
                                    berücksichtigen
                                  </Typography>

                                  <Switch
                                    id="includeDHW"
                                    checked={
                                      heatPumpFormik.values.includeDHW === "yes"
                                    }
                                    onChange={(e) =>
                                      heatPumpFormik.setFieldValue(
                                        "includeDHW",
                                        e.target.checked ? "yes" : "no",
                                        false,
                                      )
                                    }
                                    sx={{
                                      "& .MuiSwitch-switchBase.Mui-checked": {
                                        color: "#fff",
                                      },
                                      "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                                        {
                                          bgcolor: "#1D9E75",
                                          opacity: 1,
                                        },
                                      "& .MuiSwitch-switchBase + .MuiSwitch-track":
                                        {
                                          bgcolor: "#E24B4A",
                                          opacity: 1,
                                        },
                                    }}
                                  />

                                  <Typography
                                    variant="body2"
                                    sx={{
                                      minWidth: 38,
                                      fontWeight: 600,
                                      color:
                                        heatPumpFormik.values.includeDHW ===
                                        "yes"
                                          ? "#0F6E56"
                                          : "#791F1F",
                                    }}
                                  >
                                    {heatPumpFormik.values.includeDHW === "yes"
                                      ? "Ja"
                                      : "Nein"}
                                  </Typography>
                                </Box>
                              </Box>
                            </AccordionSummary>

                            <AccordionDetails>
                              {heatPumpFormik.values.includeDHW === "yes" && (
                                <Grid container spacing={1}>
                                  {/* Zapf Temperature */}
                                  <Grid item xs={12} md={4}>
                                    <FormikTextField
                                      formik={heatPumpFormik}
                                      name="pwhTemp"
                                      label="Zapftemperatur (°C)"
                                      type="number"
                                      tooltip="Temperatur am Zapfpunkt (z.B. Dusche)."
                                    />
                                  </Grid>

                                  {/* Cold Water Temperature */}
                                  <Grid item xs={12} md={4}>
                                    <FormikTextField
                                      formik={heatPumpFormik}
                                      name="coldWaterTemp"
                                      label="Kaltwassertemperatur (°C)"
                                      type="number"
                                      tooltip="Temperatur des Kaltwassers (z.B. 10 °C)"
                                    />
                                  </Grid>

                                  {/* DHW Calculation Method */}
                                  <Grid item xs={12} md={4}>
                                    <FormikTextField
                                      formik={heatPumpFormik}
                                      name="dhwCalculation"
                                      label="TWW Berechnung nach"
                                      select
                                      tooltip="1. Basierend auf dem TWW-Bedarf in kWh pro Tag und Wohneinheit 2. Nach dem standardisierten TWW-Bedarf in kWh Person und Tag"
                                    >
                                      <MenuItem value="units">
                                        Wohneinheiten
                                      </MenuItem>
                                      <MenuItem value="persons">
                                        Personenzahl
                                      </MenuItem>
                                    </FormikTextField>
                                  </Grid>
                                  <Grid item xs={12} md={4}>
                                    <FormikTextField
                                      formik={heatPumpFormik}
                                      name="versorgungskonzept"
                                      label="Versorgungskonzept"
                                      select
                                      tooltip="Bitte wählen sie ein geeignete Verteilungsoption für TWW und Raumheizung aus."
                                    >
                                      <MenuItem value="wohnungsstation">
                                        Wohnungsstation
                                      </MenuItem>
                                      <MenuItem value="frischwasserstation">
                                        Frischwasserstation
                                      </MenuItem>
                                      <MenuItem value="storageDHW">
                                        Speicher mit interne Wärmeübertrager
                                      </MenuItem>
                                    </FormikTextField>
                                  </Grid>

                                  {/* Circulation Losses */}
                                  <Grid item xs={12} md={4}>
                                    <Box
                                      display="flex"
                                      alignItems="center"
                                      sx={{ minHeight: 50 }}
                                    >
                                      <FormLabel htmlFor="circulationLosses">
                                        Zirkulationsverluste
                                      </FormLabel>
                                      <InfoTooltip title="Bitte wählen Sie aus, ob Zirkulationsverluste berücksichtigt werden sollen" />
                                    </Box>

                                    <FormControl component="fieldset">
                                      <RadioGroup
                                        row
                                        name="circulationLosses"
                                        value={
                                          heatPumpFormik.values
                                            .circulationLosses
                                        }
                                        onChange={heatPumpFormik.handleChange}
                                      >
                                        <FormControlLabel
                                          value="yes"
                                          control={<Radio />}
                                          label="Ja"
                                        />
                                        <FormControlLabel
                                          value="no"
                                          control={<Radio />}
                                          label="Nein"
                                        />
                                      </RadioGroup>
                                    </FormControl>
                                  </Grid>
                                </Grid>
                              )}
                            </AccordionDetails>
                          </Accordion>
                        </Grid>
                      </Grid>
                      {/* Button row*/}
                      <Grid item xs={12}>
                        <Box display="flex" justifyContent="flex-end" mt={1}>
                          <Button
                            variant="contained"
                            onClick={async () => {
                              setActiveCalculation("wp");
                              await heatPumpFormik.submitForm();
                            }}
                            disabled={loading}
                            size="large"
                          >
                            Berechnen
                          </Button>
                        </Box>
                      </Grid>
                    </Grid>
                  </Grid>
                </form>
              </Box>
            )}
            {/* PVT section (old activeStep === 1 block) */}
            {showPVT && (
              <Box id="pvt-step" sx={{ scrollMarginTop: "210px", mb: 6 }}>
                {/* Form */}
                <form onSubmit={pvtFormik.handleSubmit}>
                  <Typography
                    variant="h4"
                    sx={{ color: "primary.main", fontWeight: 600, mb: 2 }}
                  >
                    Auslegungsparameter für PVT-Kollektoren
                  </Typography>
                  <Grid container spacing={3} alignItems="flex-start">
                    {/* LEFT side */}
                    <Grid item xs={12} lg={12}>
                      <Grid container spacing={3} alignItems="flex-start">
                        {/* { Accordion 1} */}
                        <Grid item xs={12}>
                          <Accordion
                            expanded={expanded.pvtModule}
                            onChange={handleAccordionChange("pvtModule")}
                            sx={accordionSx}
                          >
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                              <Typography variant="h6">PVT Modul</Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                              <Grid container spacing={3}>
                                <Grid item xs={12} md={4}>
                                  <Box
                                    display="flex"
                                    alignItems="center"
                                    mb={6}
                                  >
                                    <FormLabel>Hersteller</FormLabel>
                                    <InfoTooltip
                                      title="Hier können Sie den PVT-Typ für die Wärmepumpenanlage auswählen:
                                  PVT mit rückseitigem Luft-Wärmetauscher (WISC) oder
                                  Standard PVT (WISC)"
                                    />
                                  </Box>
                                  <Select
                                    fullWidth
                                    id="pvtManufacturer"
                                    name="pvtManufacturer"
                                    value={pvtFormik.values.pvtManufacturer}
                                    onChange={(event) => {
                                      // update formik field
                                      pvtFormik.handleChange(event);
                                      // immediately submit the form
                                      //pvtFormik.handleSubmit();
                                    }}
                                    label="pvtManufacturer"
                                  >
                                    <MenuItem value="DualSun_Finned">DualSun Finned</MenuItem>
                                    <MenuItem value="TripleSolar">
                                      Triple Solar
                                    </MenuItem>
                                    {/* <MenuItem value="TWL">TWL</MenuItem> */}
                                    {/* <MenuItem value="ndSysteme">
                                      ndSysteme
                                    </MenuItem> */}
                                    <MenuItem value="Greenonetec">
                                      Greenonetec
                                    </MenuItem>
                                    {/* <MenuItem value="2Power">
                                      2Power
                                    </MenuItem> */}
                                    <MenuItem value="Consolar">
                                      Consolar
                                    </MenuItem>
                                    <MenuItem value="DualSun_Standard">DualSun Standard</MenuItem>
                                    <MenuItem value="SunMaxx">Sunmaxx</MenuItem>
                                  </Select>
                                </Grid>
                                <Grid item xs={12} md={4}>
                                  <FormikTextField
                                    formik={heatPumpFormik}
                                    name="Leistungsminderungsfaktor"
                                    label="Leistungsminderungsfaktor"
                                    type="number"
                                    minHeight={68}
                                    tooltip="Dieser Faktor berücksichtigt zusätzliche Einflüsse auf die Anlagenleistung, insbesondere bei Großanlagen. Dazu zählen unter anderem die Installationsart (beispielsweise aufgeständert oder auf dem Dach montiert) sowie die Gebäudehöhe. Diese Faktoren können die Windanströmung und somit die Leistung beeinflussen. Es wird ein Wert zwischen 0.15 und 0.20 vorgeschlagen."
                                  />
                                </Grid>
                              </Grid>
                            </AccordionDetails>
                          </Accordion>
                        </Grid>
                        {/* Accordion 2 */}
                        <Grid item xs={12}>
                          <Accordion
                            expanded={expanded.heatPumpSpecs}
                            onChange={handleAccordionChange("heatPumpSpecs")}
                            id="heatPumpSpecs"
                            sx={accordionSx}
                          >
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                              <Typography variant="h6">
                                Auswahl einer Wärmepumpe
                              </Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                              <Grid container spacing={3}>
                                {/* Operating mode */}
                                <Grid item xs={12} md={4}>
                                  <Box
                                    display="flex"
                                    alignItems="center"
                                    mb={0.5}
                                    sx={{ minHeight: 48 }}
                                  >
                                    <FormLabel htmlFor="operationMode">
                                      Betriebsmodus
                                    </FormLabel>
                                    <InfoTooltip title="Der Betriebsmodus der Wärmepumpe basiert auf der Deckung des Wärmebedarfs" />
                                  </Box>
                                  <Select
                                    fullWidth
                                    id="operationMode"
                                    name="operationMode"
                                    value={pvtFormik.values.operationMode}
                                    onChange={pvtFormik.handleChange}
                                  >
                                    <MenuItem value="monovalent">
                                      Monovalent
                                    </MenuItem>
                                    <MenuItem value="bivalent-alternative">
                                      Bivalent Alternative
                                    </MenuItem>
                                    <MenuItem value="bivalent-parallel">
                                      Bivalent Parallel
                                    </MenuItem>
                                  </Select>
                                </Grid>
                                {!isModeSelected && (
                                  <Grid item xs={12}>
                                    <Alert severity="info">
                                      Bitte wählen Sie einen Betriebsmodus, um
                                      die WP-Betriebspunkte anzuzeigen.
                                    </Alert>
                                  </Grid>
                                )}
                                {/* Minimum evaporator inlet temperature */}
                                <Grid item xs={12} md={4}>
                                  <FormikTextField
                                    formik={pvtFormik}
                                    name="minEvaporatorTemp"
                                    label="Minimale Verdampfereintrittstemperatur (°C)"
                                    type="number"
                                    minHeight={48}
                                    tooltip="Geben Sie die minimale Eintrittstemperatur an, bei der die WP betrieben werden darf"
                                  />
                                </Grid>

                                <Grid item xs={12}>
                                  <Box sx={{ mt: 0.5 }}>
                                    <Divider />
                                  </Box>
                                </Grid>
                                {/* --- Dynamic Set 1: For Monovalent, Bivalent-Parallel and Empty --- */}
                                {opMode === "monovalent" && (
                                  <>
                                    <OpPointRow>
                                      <TextField
                                        fullWidth
                                        label="B (°C)"
                                        id="minEvaporatorTemp"
                                        name="minEvaporatorTemp"
                                        value={
                                          pvtFormik.values.minEvaporatorTemp
                                        }
                                        onChange={pvtFormik.handleChange}
                                        onWheel={(e) => e.target.blur()}
                                        InputLabelProps={{ shrink: true }}
                                      />

                                      <TextField
                                        fullWidth
                                        label="W (°C)"
                                        value={getWValue()}
                                        InputProps={{ readOnly: true }}
                                        InputLabelProps={{ shrink: true }}
                                      />

                                      <TextField
                                        fullWidth
                                        id="cop"
                                        name="cop"
                                        label="COP"
                                        type="number"
                                        value={pvtFormik.values.cop}
                                        onChange={pvtFormik.handleChange}
                                        onWheel={(e) => e.target.blur()}
                                        InputLabelProps={{ shrink: true }}
                                      />

                                      <TextField
                                        fullWidth
                                        id="nominalPower"
                                        name="nominalPower"
                                        label="Nennleistung (kW)"
                                        type="number"
                                        value={pvtFormik.values.nominalPower}
                                        onChange={pvtFormik.handleChange}
                                        onWheel={(e) => e.target.blur()}
                                        InputLabelProps={{ shrink: true }}
                                      />
                                    </OpPointRow>
                                  </>
                                )}
                                {/* --- Dynamic Set 2: For  Bivalent-Alternative --- */}

                                {opMode === "bivalent-alternative" && (
                                  <>
                                    <OpPointRow>
                                      <TextField
                                        fullWidth
                                        label="B (°C)"
                                        id="minEvaporatorTemp"
                                        name="minEvaporatorTemp"
                                        value={
                                          pvtFormik.values.minEvaporatorTemp
                                        }
                                        onChange={pvtFormik.handleChange}
                                        onWheel={(e) => e.target.blur()}
                                        InputLabelProps={{ shrink: true }}
                                      />

                                      <TextField
                                        fullWidth
                                        label="W (°C)"
                                        value={getWValue()}
                                        InputProps={{ readOnly: true }}
                                        InputLabelProps={{ shrink: true }}
                                      />

                                      <TextField
                                        fullWidth
                                        id="cop"
                                        name="cop"
                                        label="COP"
                                        type="number"
                                        value={pvtFormik.values.cop}
                                        onChange={pvtFormik.handleChange}
                                        onWheel={(e) => e.target.blur()}
                                        InputLabelProps={{ shrink: true }}
                                      />

                                      <TextField
                                        fullWidth
                                        id="nominalPower"
                                        name="nominalPower"
                                        label="Nennleistung (kW)"
                                        type="number"
                                        value={pvtFormik.values.nominalPower}
                                        onChange={pvtFormik.handleChange}
                                        onWheel={(e) => e.target.blur()}
                                        InputLabelProps={{ shrink: true }}
                                      />
                                    </OpPointRow>
                                  </>
                                )}
                                {/* --- Dynamic Set 3: For  Bivalent-Parallel and Empty --- */}
                                {opMode === "bivalent-parallel" && (
                                  <>
                                    <OpPointLabel
                                      text="Betriebspunkt 1"
                                      tooltip="Die Leistungsdaten wurden für die Kalkulation des Heizstabs verwendet. Dieser Punkt ist eigentlich der Abschaltpunkt für die Wärmepumpe, d.h. Mindestverdampfereintrittstemperatur der Wärmepumpe."
                                    />
                                    <OpPointRow>
                                      <TextField
                                        fullWidth
                                        label="B (°C)"
                                        // id="minEvaporatorTemp"
                                        // name="minEvaporatorTemp"
                                        value={
                                          pvtFormik.values.minEvaporatorTemp
                                        }
                                        //onChange={pvtFormik.handleChange}
                                        InputProps={{ readOnly: true }}
                                        //onWheel={(e) => e.target.blur()}
                                        InputLabelProps={{ shrink: true }}
                                        helperText="Wird automatisch aus der minimale Verdampfereintrittstemperatur übernommen"
                                      />

                                      <TextField
                                        fullWidth
                                        label="W (°C)"
                                        value={getWValue()}
                                        InputProps={{ readOnly: true }}
                                        InputLabelProps={{ shrink: true }}
                                      />

                                      <TextField
                                        fullWidth
                                        id="cop"
                                        name="cop"
                                        label="COP"
                                        type="number"
                                        value={pvtFormik.values.cop}
                                        onChange={pvtFormik.handleChange}
                                        onWheel={(e) => e.target.blur()}
                                        InputLabelProps={{ shrink: true }}
                                      />

                                      <TextField
                                        fullWidth
                                        id="nominalPower"
                                        name="nominalPower"
                                        label="Nennleistung (kW)"
                                        type="number"
                                        value={pvtFormik.values.nominalPower}
                                        onChange={pvtFormik.handleChange}
                                        onWheel={(e) => e.target.blur()}
                                        InputLabelProps={{ shrink: true }}
                                      />
                                    </OpPointRow>

                                    <OpPointLabel
                                      text="Betriebspunkt 2"
                                      tooltip="Die Leistungsdaten wurden für die Kalkulation der PVT-Kollektorfläche verwendet. Dieser Punkt ist eigentlich als der Auslegungspunkt für die PVT-Kollektoren verwendet und kann auch die der Bivalenzpunkt sein."
                                    />
                                    <OpPointRow>
                                      <TextField
                                        fullWidth
                                        id="bivalentParallelB"
                                        name="bivalentParallelB"
                                        label="B (°C)"
                                        type="number"
                                        value={
                                          pvtFormik.values.bivalentParallelB ||
                                          ""
                                        }
                                        onChange={pvtFormik.handleChange}
                                        onWheel={(e) => e.target.blur()}
                                        InputLabelProps={{ shrink: true }}
                                      />

                                      <TextField
                                        fullWidth
                                        label="W (°C)"
                                        value={getWValue()}
                                        InputProps={{ readOnly: true }}
                                        InputLabelProps={{ shrink: true }}
                                      />

                                      <TextField
                                        fullWidth
                                        id="cop_2"
                                        name="cop_2"
                                        label="COP"
                                        type="number"
                                        value={pvtFormik.values.cop_2 || ""}
                                        onChange={(e) => {
                                          setCop2ManuallyEdited(true);
                                          pvtFormik.handleChange(e);
                                        }}
                                        onWheel={(e) => e.target.blur()}
                                        InputLabelProps={{ shrink: true }}
                                        helperText={
                                          cop2ManuallyEdited
                                            ? "Manuell angepasst"
                                            : "Automatisch vorgeschlagen, kann überschrieben werden"
                                        }
                                      />

                                      <TextField
                                        fullWidth
                                        id="nominalPower_2"
                                        name="nominalPower_2"
                                        label="Nennleistung (kW)"
                                        type="number"
                                        value={
                                          pvtFormik.values.nominalPower_2 || ""
                                        }
                                        onChange={pvtFormik.handleChange}
                                        onBlur={pvtFormik.handleBlur}
                                        onWheel={(e) => e.target.blur()}
                                        InputLabelProps={{ shrink: true }}
                                        error={
                                          pvtFormik.touched.nominalPower_2 &&
                                          Boolean(
                                            pvtFormik.errors.nominalPower_2,
                                          )
                                        }
                                        helperText={
                                          pvtFormik.touched.nominalPower_2 &&
                                          pvtFormik.errors.nominalPower_2
                                        }
                                      />
                                    </OpPointRow>
                                  </>
                                )}
                              </Grid>
                            </AccordionDetails>
                          </Accordion>
                        </Grid>
                      </Grid>
                      <Grid item xs={12}>
                        <Box display="flex" justifyContent="flex-end" mt={1}>
                          <Button
                            variant="contained"
                            onClick={async () => {
                              setActiveCalculation("system");
                              await pvtFormik.submitForm();
                            }}
                            disabled={SystemCalcDisabled}
                            size="large"
                          >
                            Geänderte Werte übernehmen
                          </Button>
                        </Box>
                      </Grid>
                    </Grid>
                  </Grid>
                </form>
              </Box>
            )}
            {/* EWS section (styled like PVT section) */}
            {showEWS && (
              <Box
                id="ews-step"
                sx={{ scrollMarginTop: "260px", pt: "32px", mb: 6 }}
              >
                <form onSubmit={bheFormik2.handleSubmit}>
                  <Typography
                    variant="h4"
                    sx={{
                      color: "primary.main",
                      fontWeight: 600,
                      mt: 2,
                      mb: 2,
                    }}
                  >
                    Auslegung der Erdwärmesonden (EWS)
                  </Typography>
                  <Grid container spacing={3}>
                    <Grid
                      container
                      spacing={3}
                      alignItems="flex-start"
                      sx={{ mt: 1 }}
                    >
                      {/* LEFT side (form) */}
                      <Grid item xs={12} lg={12}>
                        <Grid container spacing={3}>
                          {/* Accordion 1: Wärmepumpensystem */}
                          <Grid item xs={12}>
                            <Accordion
                              expanded={expanded.bheSystem}
                              onChange={handleAccordionChange("bheSystem")}
                              id="bheSystem"
                              sx={accordionSx}
                            >
                              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Typography variant="h6">
                                  Wärmepumpensystem
                                </Typography>
                              </AccordionSummary>

                              <AccordionDetails>
                                <Grid container spacing={3}>
                                  <Grid item xs={12} md={6}>
                                    <FormikTextField
                                      formik={bheFormik2}
                                      name="designTemp"
                                      label="Auslegungstemperatur (°C)"
                                      type="number"
                                      tooltip="The temperature at the inlet of the bhe field used as design point"
                                    />
                                  </Grid>

                                  <Grid item xs={12} md={6}>
                                    <FormikTextField
                                      formik={bheFormik2}
                                      name="QWP"
                                      label="WP-Wärmeleistung (kW)"
                                      type="number"
                                      tooltip="Heat pump output at design point"
                                    />
                                  </Grid>

                                  <Grid item xs={12} md={6}>
                                    <FormikTextField
                                      formik={bheFormik2}
                                      name="cop_WP"
                                      label="COP der WP"
                                      type="number"
                                      tooltip="COP of the heat pump"
                                    />
                                  </Grid>

                                  <Grid item xs={12} md={6}>
                                    <FormikTextField
                                      formik={bheFormik2}
                                      name="QWP_year"
                                      label="Jahreswärmebereitstellung (kWh)"
                                      type="number"
                                      tooltip="Heat pump heat output per year"
                                    />
                                  </Grid>

                                  <Grid item xs={12} md={6}>
                                    <FormikTextField
                                      formik={bheFormik2}
                                      name="temperatureSpread"
                                      label="Spreizung am EWS-Feld (K)"
                                      type="number"
                                      tooltip="Die Temperaturdifferenz (zwischen Vor- und Rücklauf) am EWS-Feld oder am WP-Verdampfer"
                                    />
                                  </Grid>

                                  <Grid item xs={12} md={6}>
                                    <Box
                                      display="flex"
                                      alignItems="center"
                                      mb={0.5}
                                    >
                                      <FormLabel component="legend">
                                        Betriebsart
                                      </FormLabel>
                                      <InfoTooltip title="Wählen Sie die Betriebsart: Nur Heizen oder Heizen + TWW" />
                                    </Box>
                                    <FormControl component="fieldset" fullWidth>
                                      <RadioGroup
                                        row
                                        name="hasDHW"
                                        value={bheFormik2.values.hasDHW}
                                        onChange={bheFormik2.handleChange}
                                      >
                                        <FormControlLabel
                                          value={false}
                                          control={<Radio />}
                                          label="Nur Heizen"
                                        />
                                        <FormControlLabel
                                          value={true}
                                          control={<Radio />}
                                          label="Heizen + TWW"
                                        />
                                      </RadioGroup>
                                    </FormControl>
                                  </Grid>
                                </Grid>
                              </AccordionDetails>
                            </Accordion>
                          </Grid>

                          {/* Accordion 2: Bodenbeschaffenheit */}
                          <Grid item xs={12}>
                            <Accordion
                              expanded={expanded.groundProperties}
                              onChange={handleAccordionChange(
                                "groundProperties",
                              )}
                              id="groundProperties"
                              sx={accordionSx}
                            >
                              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Typography variant="h6">
                                  Bodenbeschaffenheit
                                </Typography>
                              </AccordionSummary>

                              <AccordionDetails>
                                <Grid container spacing={3}>
                                  <Grid item xs={12} md={6}>
                                    <FormikTextField
                                      formik={bheFormik2}
                                      name="lambda_E"
                                      label="Wärmeleitfähigkeit (W/m·K)"
                                      type="number"
                                      tooltip="Wärmeleitfähigkeit des Untergrunds λ, der anhand des Geothermischen Reaktionstests (GRT) ermittelt wurde"
                                      required
                                    />
                                  </Grid>

                                  <Grid item xs={12} md={6}>
                                    <FormikTextField
                                      formik={bheFormik2}
                                      name="resistance"
                                      label="Thermische Widerstand (m·K/W)"
                                      type="number"
                                      tooltip="Der effektive thermische Widerstand des Bohrlochs, der anhand des Geothermischen Reaktionstests (GRT) ermittelt wurde."
                                      required
                                    />
                                  </Grid>

                                  <Grid item xs={12} md={6}>
                                    <FormikTextField
                                      formik={bheFormik2}
                                      name="heatCapacity"
                                      label="Vol. Wärmekapazität (MJ/(m³·K))"
                                      type="number"
                                      tooltip="Volumetrische Wärmekapazität des Untergrunds, der auch von Geothermischen Reaktionstests (GRT) ermittelt wurde"
                                      required
                                    />
                                  </Grid>

                                  <Grid item xs={12} md={6}>
                                    <FormikTextField
                                      formik={bheFormik2}
                                      name="heatFlux"
                                      label="Terr. Wärmestromdichte (W/m²)"
                                      type="number"
                                      tooltip="Wärme vom Erdinneren zur Erdoberfläche (0.055–0.112 W/m²)"
                                      required
                                    />
                                  </Grid>
                                </Grid>
                              </AccordionDetails>
                            </Accordion>
                          </Grid>

                          {/* Accordion 3: Erdsondenfeld */}
                          <Grid item xs={12}>
                            <Accordion
                              expanded={expanded.fieldProperties}
                              onChange={handleAccordionChange(
                                "fieldProperties",
                              )}
                              id="fieldProperties"
                              sx={accordionSx}
                            >
                              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Typography variant="h6">
                                  Erdsondenfeld
                                </Typography>
                              </AccordionSummary>

                              <AccordionDetails>
                                <Grid container spacing={3}>
                                  {/* <Grid item xs={12} md={4}>
                              <FormikTextField
                                formik={bheFormik2}
                                name="configuration"
                                label="Feldconfiguration"
                                select
                                tooltip="Die gewünschte Anordnung der Erdwärmesonden im Feld für die Berechnung der g-functions"
                                required
                              >
                                <MenuItem value="Rectangular">Rechteckig</MenuItem>

                                {/* <MenuItem value="Linear">Linear</MenuItem>
                                <MenuItem value="Rectangular">Rechteckig</MenuItem>
                                <MenuItem value="L-shaped">L-förmig</MenuItem>
                                <MenuItem value="U-shaped">U-förmig</MenuItem> */}
                                  {/* </FormikTextField>
                            </Grid> */}

                                  <Grid item xs={12} md={4}>
                                    <FormikTextField
                                      formik={bheFormik2}
                                      name="lEWS"
                                      label="Max. EWS-Tiefe (m)"
                                      type="number"
                                      tooltip="Die maximal zulässige oder gewünschte Tiefe einer einzelnen Erdwärmesonde"
                                      required
                                    />
                                  </Grid>

                                  <Grid item xs={12} md={4}>
                                    <FormikTextField
                                      formik={bheFormik2}
                                      name="bEWS"
                                      label="Sondenabstand (m)"
                                      type="number"
                                      tooltip="Abstand zwischen den einzelnen Erdwärmesonden"
                                      required
                                    />
                                  </Grid>
                                </Grid>
                              </AccordionDetails>
                            </Accordion>
                          </Grid>

                          {/* Submit button */}
                          <Grid item xs={12}>
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "center",
                                mt: 1,
                              }}
                            >
                              <Button
                                variant="contained"
                                color="primary"
                                type="submit"
                                disabled={bheLoading}
                                size="large"
                                sx={{ minWidth: 220 }}
                              >
                                {bheLoading ? (
                                  <CircularProgress size={24} />
                                ) : (
                                  "EWS-Länge Berechnen"
                                )}
                              </Button>
                            </Box>
                          </Grid>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                </form>
              </Box>
            )}
          </Box>
        </Grid>

        {/* ================= RIGHT: Schema + Results ================= */}
        {/* Schema Room Heating and Hot drinking water*/}
        <Grid item xs={12} lg={7}>
          <Box sx={{ position: "sticky", top: 120 }}>
            <Paper sx={sectionCardSx}>
              <Box sx={{ p: 2 }}>
                {isPvtMode ? (
                  <HeatingSystemDiagramImage1
                    formik={heatPumpFormik}
                    wpResults={wpDesignResults}
                    pvtArea={displayedPVTArea}
                    heatingRod={displayedHeatingRod}
                  />
                ) : (
                  <HeatingSystemDiagramImage2
                    formik={heatPumpFormik}
                    wpResults={wpDesignResults}
                    bheResults={bheResults}
                  />
                )}
              </Box>
            </Paper>
            <Box sx={{ mt: 2 }} />
            <DesignResultsPanel
              mode={mode}
              wpDesignResults={wpDesignResults}
              bheResults={bheResults}
              displayedHeatingLoadAtDesignPoint={
                displayedHeatingLoadAtDesignPoint
              }
              displayedPvtCollectorPower={displayedPvtCollectorPower}
              displayedRequiredPVTArea={displayedRequiredPVTArea}
              displayedHeatingRod={displayedHeatingRodFinal}
              showOversizeInfo={
                activeCalculation === "system" ? showOversizeInfo : false
              }
              showHeatingRodWarning={
                activeCalculation === "system" ? showHeatingRodWarning : false
              }
            />
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default PVTDesignForm;
